import { DateTime } from 'luxon';
import { DateFromTo } from '../../server-types';

class DateTimeService {

    private assertDate(date: string | number | Date) {
        if (typeof date !== 'number') {
            if (Number.isNaN(Date.parse(date.toString())) === true) {
                throw new Error(`Invalid date provided, ${JSON.stringify(date)}`);
            }
        }

    }

    public getNowDateTime() {
        return DateTime.local();
    }

    public getStartOfTheDay(date: DateTime) {
        return date.startOf('day');
    }

    public getEndOfTheDay(date: DateTime) {
        return date.endOf('day');
    }

    public getEndOfTheDayJsDate = (date: Date) => {
        const dateTime = this.getDateTimeFromJsDate(date);
        return this.getEndOfTheDay(dateTime).toJSDate();
    };

    public getStartOfTheDayJsDate = (date: Date) => {
        const dateTime = this.getDateTimeFromJsDate(date);
        return this.getStartOfTheDay(dateTime).toJSDate();
    };

    public getIsoDateStringFromJsDate = (date: Date) => {
        return this.getDateTimeFromJsDate(date).toISODate() as unknown as string;
    };

    public getDateTimeFromJsDate(date: Date) {
        return DateTime.fromJSDate(date);
    }

    public getDifFromDate({ from, to }: DateFromTo) {
        const start = DateTime.fromJSDate(from);
        const end = DateTime.fromJSDate(to);
        return end.diff(start, 'days');
    }

    /**
    * localizeDate: returns localized date
    */
    public localizeDate(date: string | number | Date, locale: string) {
        this.assertDate(date);
        return new Date(date).toLocaleDateString(locale);
    }

    public localizeDateWithWeekday(date: string | number | Date, locale: string) {
        this.assertDate(date);
        return new Date(date).toLocaleDateString(locale, { weekday: 'short', ...DateTime.DATE_SHORT });
    }

    /**
     * localizeDateTime: returns localized dateTime
     */
    public localizeDateTime(date: string | number | Date, locale: string) {
        this.assertDate(date);
        return new Date(date).toLocaleString(locale, { });
    }

    /**
     * localizeDateTime: returns localized dateTime
     */
    public localizeShortDateTime(date: string | number | Date, locale: string) {
        this.assertDate(date);
        return new Date(date)
            .toLocaleString(locale, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    }

    /**
     * localizeTime: returns localized time
     */
    public localizeTime(date: string | number | Date, locale: string) {
        this.assertDate(date);
        return new Date(date).toLocaleTimeString(locale);
    }

    /**
     * localizeShortTime
     */
    public localizeShortTime(date: string | number | Date, locale: string) {
        this.assertDate(date);
        return new Date(date).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    }

    public localizeShortTimeWithWeekday(date: string | number | Date, locale: string) {
        this.assertDate(date);
        return new Date(date).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', weekday: 'short' });
    }
}

export default DateTimeService;
